import React, { useState, useEffect } from 'react';
import { motion, useScroll, useSpring } from 'framer-motion';
import { Home, UserPlus, Calendar, Mail, Menu, X } from 'lucide-react';

const Navigation: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href: string) => {
    setIsMobileMenuOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleRegistrationClick = (href: string) => {
    handleNavClick(href);
  };

  const navItems = [
    { name: 'Sākums', href: '#hero', icon: <Home className="w-4 h-4" /> },
    { name: 'Pieteikšanās', href: '#registration', icon: <UserPlus className="w-4 h-4" /> },
    { name: 'Programma', href: '#program', icon: <Calendar className="w-4 h-4" /> },
    { name: 'Kontakti', href: '#contact', icon: <Mail className="w-4 h-4" /> },
  ];

  return (
    <>
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary-400 via-secondary-500 to-accent-500 z-50 origin-left"
        style={{ scaleX }}
      />
      <nav
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${isScrolled ? 'bg-white/80 backdrop-blur-md shadow-lg py-2' : 'bg-transparent py-4'
          }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="flex items-center cursor-pointer"
              onClick={() => handleNavClick('#hero')}
            >
              <div className="relative">
                <div className="absolute inset-0 bg-primary-400 rounded-full blur-md opacity-50"></div>
                <div className="relative bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full p-2 w-10 h-10 flex items-center justify-center text-white shadow-lg">
                  <span className="text-xl md:text-xl font-black leading-none">SD</span>
                </div>
              </div>
              <div className="ml-3">
                <h1 className={`text-xl md:text-2xl font-bold transition-all duration-300 ${isScrolled ? 'text-gray-900' : 'text-white drop-shadow-lg'
                  }`}>
                  Superdiena
                </h1>
                <p className={`text-xs md:text-sm transition-all duration-300 ${isScrolled ? 'text-primary-600' : 'text-gray-300'
                  }`}>
                  Ziemas piedzīvojums
                </p>
              </div>
            </motion.div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-1">
              {navItems.map((item, index) => (
                <motion.button
                  key={item.name}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleNavClick(item.href)}
                  className={`px-4 py-2 rounded-full font-medium transition-all duration-300 flex items-center gap-2 ${isScrolled
                    ? 'text-gray-700 hover:text-primary-600 hover:bg-primary-50'
                    : 'text-white drop-shadow-lg hover:text-primary-300 hover:bg-white/10'
                    }`}
                >
                  <span className="hidden lg:inline">{item.icon}</span>
                  {item.name}
                </motion.button>
              ))}
            </div>

            {/* CTA Button */}
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleRegistrationClick('#registration')}
              className="hidden md:block px-6 py-2 bg-gradient-to-r from-primary-500 to-secondary-500 text-white font-bold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:from-primary-600 hover:to-secondary-600"
            >
              Pieteikties
            </motion.button>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`p-2 rounded-lg transition-colors ${isScrolled ? 'text-gray-900' : 'text-white'
                  }`}
              >
                {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white/95 backdrop-blur-lg shadow-xl border-t border-gray-100"
          >
            <div className="container mx-auto px-4 py-6 flex flex-col space-y-4">
              {navItems.map((item) => (
                <button
                  key={item.name}
                  onClick={() => handleNavClick(item.href)}
                  className="text-left py-3 px-4 rounded-xl text-gray-800 font-semibold hover:bg-primary-50 hover:text-primary-600 transition-colors flex items-center gap-3"
                >
                  <span className="text-primary-500">{item.icon}</span>
                  {item.name}
                </button>
              ))}
              <button
                onClick={() => handleRegistrationClick('#registration')}
                className="w-full py-4 mt-4 bg-gradient-to-r from-primary-500 to-secondary-500 text-white font-bold rounded-xl shadow-lg"
              >
                Pieteikties
              </button>
            </div>
          </motion.div>
        )}
      </nav>
    </>
  );
};

export default Navigation;
